import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Wrench, Paintbrush, Cog, Zap, Shield, Car, Box, Pencil, Scan } from "lucide-react";
import { Button } from "@/components/ui/button";

const customBuildServices = [
{
  icon: Car,
  title: "GROUND-UP BUILDS",
  description: "Complete custom builds from the ground up. Every nut, bolt, and panel receives meticulous attention to bring your vision to life."
},
{
  icon: Cog,
  title: "CUSTOM FABRICATION",
  description: "Custom metal fabrication, wide body fender flares, body mods, and panel fitment. Our in-house fabrication shop can build whatever your project requires."
},
{
  icon: Wrench,
  title: "SUSPENSION & HANDLING",
  description: "We work with premium brands to come up with solutions to meet your driving style."
}];


const coatingServices = [
{
  icon: Shield,
  title: "Cerakote\xAE COATINGS",
  description: "Extreme heat & corrosion resistance, durable, long-lasting finish. Ideal for engine components, chassis & custom parts. Thin film builds allow for many things to be coated including fasteners"
},
{
  icon: Zap,
  title: "HIGH-TEMP COATINGS",
  description: "Specialized coatings for headers, exhaust, and engine components. Withstands extreme heat while looking great."
},
{
  icon: Paintbrush,
  title: "CUSTOM PAINT",
  description: "Premium finishes include classic single stage and satin coats to modern basecoat/clearcoat applications."
}];


const designServices = [
{
  icon: Pencil,
  title: "SKETCH TO PART",
  description: "Bring us your idea on paper. We'll model it in 3D, refine the design, and fabricate the finished piece — bridging old-school creativity with modern precision."
},
{
  icon: Scan,
  title: "SCAN TO PART",
  description: "Using our Revopoint MetroX blue-laser scanner, we capture existing parts in fine detail. Perfect for replicating discontinued components, reverse engineering, or designing parts that bolt up the first time."
}];


export default function ServicesPage() {
  return (
    <div className="min-h-screen pt-20" data-testid="services-page">
      {/* Hero Section */}
      <section className="relative py-24 bg-zinc-950">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1511268767685-02638fbfb6e5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwyfHxkYXJrJTIwZ2FyYWdlJTIwYmFja2dyb3VuZCUyMGdydW5nZXxlbnwwfHx8fDE3NzU4MzgxNTl8MA&ixlib=rb-4.1.0&q=85"
            alt="Garage"
            className="w-full h-full object-cover opacity-30" />

          <div className="absolute inset-0 bg-gradient-to-b from-zinc-950 via-zinc-950/80 to-zinc-950" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl">

            <span className="font-mono text-xs tracking-[0.3em] text-amber-500 uppercase mb-4 block">
              OUR SERVICES
            </span>
            <h1 className="font-heading text-5xl sm:text-6xl md:text-7xl text-white tracking-tighter mb-6">
              GOOD DESIGN<br />
              <span className="text-amber-500">REFINED BY DETAIL</span>
            </h1>
            <p className="font-body text-lg text-zinc-300 leading-relaxed">
              From complete custom builds to specialty coatings and 3D design & parts fabrication, we offer comprehensive services for enthusiasts who demand the best. Every project receives our full attention and expertise.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Custom Builds */}
      <section className="py-24 bg-[#09090b]" data-testid="custom-builds-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Left - Image */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative">

              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_hot-rod-coatings/artifacts/n7xk1ijd_001808A8-56ED-499C-BF8D-0700EBFADFA9_1_102_o.jpeg"
                  alt="Custom build work"
                  className="w-full h-full object-cover" />

              </div>
              <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-amber-500 flex items-center justify-center">
                <span className="font-heading text-5xl text-black">25+</span>
                <span className="font-mono text-xs text-black ml-2">YEARS<br />EXP.</span>
              </div>
            </motion.div>

            {/* Right - Content */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}>

              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-navy flex items-center justify-center">
                  <Wrench size={24} className="text-amber-500" />
                </div>
                <span className="font-mono text-xs tracking-[0.2em] text-zinc-500 uppercase">
                  DIVISION ONE
                </span>
              </div>
              
              <h2 className="font-heading text-4xl sm:text-5xl text-white tracking-tight mb-6">
                CUSTOM<br />
                <span className="text-amber-500">BUILDS</span>
              </h2>
              
              <p className="font-body text-zinc-400 leading-relaxed mb-8">
                We specialize in turning your vision into reality. Whether you're looking for a ground-up custom build or a complete transformation, our team has the skills and passion to exceed your expectations.
              </p>

              <div className="space-y-6">
                {customBuildServices.map((service, index) =>
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="flex gap-4 p-4 border border-white/10 bg-zinc-900/50 hover:border-amber-500/30 transition-colors">

                    <service.icon size={24} className="text-amber-500 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-heading text-lg text-white mb-1">{service.title}</h4>
                      <p className="font-body text-sm text-zinc-400">{service.description}</p>
                    </div>
                  </motion.div>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Specialty Coatings */}
      <section className="py-24 bg-zinc-950 border-t border-white/10" data-testid="coatings-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Left - Content */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-2 lg:order-1">

              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-navy flex items-center justify-center">
                  <Paintbrush size={24} className="text-amber-500" />
                </div>
                <span className="font-mono text-xs tracking-[0.2em] text-zinc-500 uppercase">
                  DIVISION TWO
                </span>
              </div>
              
              <h2 className="font-heading text-4xl sm:text-5xl text-white tracking-tight mb-6">
                SPECIALTY<br />
                <span className="text-amber-500">COATINGS</span>
              </h2>
              
              <p className="font-body text-zinc-400 leading-relaxed mb-8">Our coatings division delivers professional-grade finishes for automotive and industrial applications. We have experience in many industries to help us find the perfect coating for your automotive application needs.</p>

              <div className="space-y-6">
                {coatingServices.map((service, index) =>
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="flex gap-4 p-4 border border-white/10 bg-zinc-900/50 hover:border-amber-500/30 transition-colors">

                    <service.icon size={24} className="text-amber-500 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-heading text-lg text-white mb-1">{service.title}</h4>
                      <p className="font-body text-sm text-zinc-400">{service.description}</p>
                    </div>
                  </motion.div>
                )}
              </div>
            </motion.div>

            {/* Right - Image */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative order-1 lg:order-2">

              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_hot-rod-coatings/artifacts/0f2j3ehp_325E31AA-697D-4ABF-9EEF-7AD8D52CC8F0_1_105_c.jpeg"
                  alt="Specialty coatings"
                  className="w-full h-full object-cover" />

              </div>
              <div className="absolute -bottom-6 -left-6 w-48 h-48 bg-navy flex items-center justify-center">
                <span className="font-heading text-5xl text-amber-500">100%</span>
                <span className="font-mono text-xs text-white ml-2">QUALITY<br />ASSURED</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 3D Design & Parts Fabrication */}
      <section className="py-24 bg-[#09090b] border-t border-white/10" data-testid="design-fab-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Left - Image */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative">

              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_hot-rod-coatings/artifacts/639vj7wo_3D%20Print%20Design%20Hero%20Image.jpeg"
                  alt="3D scanning and parts fabrication"
                  className="w-full h-full object-cover" />

              </div>
              <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-amber-500 flex items-center justify-center">
                <span className="font-heading text-5xl text-black">0.02</span>
                <span className="font-mono text-xs text-black ml-2">MM<br />ACCURACY</span>
              </div>
            </motion.div>

            {/* Right - Content */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}>

              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-navy flex items-center justify-center">
                  <Box size={24} className="text-amber-500" />
                </div>
                <span className="font-mono text-xs tracking-[0.2em] text-zinc-500 uppercase">
                  DIVISION THREE
                </span>
              </div>

              <h2 className="font-heading text-4xl sm:text-5xl text-white tracking-tight mb-6">
                3D DESIGN<br />
                <span className="text-amber-500">& PARTS FAB</span>
              </h2>

              <p className="font-body text-zinc-400 leading-relaxed mb-8">
                From hand-drawn sketch to finished part. Powered by our Revopoint MetroX blue-laser 3D scanner, we capture existing components at metrology-grade accuracy and design custom parts that fit right the first time — bridging old-school creativity with modern precision.
              </p>

              <div className="space-y-6">
                {designServices.map((service, index) =>
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="flex gap-4 p-4 border border-white/10 bg-zinc-900/50 hover:border-amber-500/30 transition-colors">

                    <service.icon size={24} className="text-amber-500 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-heading text-lg text-white mb-1">{service.title}</h4>
                      <p className="font-body text-sm text-zinc-400">{service.description}</p>
                    </div>
                  </motion.div>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-amber-500" data-testid="services-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}>

            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-black tracking-tight mb-6">
              LET'S BUILD<br />SOMETHING GREAT
            </h2>
            <p className="font-body text-lg text-black/70 mb-8 max-w-2xl mx-auto">
              Every project starts with a conversation. Tell us about your vision, and we'll help make it a reality.
            </p>
            <Link to="/quote">
              <Button
                className="bg-black text-white font-heading text-xl tracking-wide px-10 py-6 rounded-none hover:bg-zinc-900 transition-all duration-200"
                data-testid="services-cta-btn">

                LET'S TALK
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>);

}